import { useNavigate } from "react-router-dom";
import { useLocalization } from "../../hooks/useLocalization";
import { useLocalePath } from "../../hooks/useLocalePath";
import { ITeamMatchHistoryEntry, ITeamMatchHistoryGame } from "../../types/types";
import { championIconUrl } from "../../utils/championStats";
import { formatMatchDate } from "../../utils/formatMatchDate";
import { matchDetailUrl } from "../../utils/matchDetail";
import { getMatchTypeLocalized } from "../../services/utils/utils";
import { TeamLogo } from "../shared/TeamLogo";

interface TeamMatchHistoryProps {
  teamId: string;
  teamName: string;
  entries: ITeamMatchHistoryEntry[];
}

const emptyTextClassName = "m-0 font-shentox text-white/72";

const outcomeFromResult = (result: string): "win" | "loss" | "draw" => {
  if (result === "WIN") {
    return "win";
  }
  if (result === "LOSS") {
    return "loss";
  }
  return "draw";
};

const champGroupClassName: Record<"win" | "loss" | "draw", string> = {
  win: "bg-[rgba(94,234,212,0.1)] shadow-[inset_0_0_0_1px_rgba(94,234,212,0.45)] [&_img]:border-[rgba(94,234,212,0.55)]",
  loss: "bg-[rgba(248,113,113,0.1)] shadow-[inset_0_0_0_1px_rgba(248,113,113,0.45)] [&_img]:border-[rgba(248,113,113,0.55)]",
  draw: "bg-white/4 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]",
};

const ChampGroup = ({
  champions,
  outcome,
  label,
  matchId,
  gameKey,
  side,
}: {
  champions: string[];
  outcome: "win" | "loss" | "draw";
  label: string;
  matchId: number;
  gameKey: string;
  side: "team" | "opponent";
}) => {
  if (!champions.length) {
    return null;
  }

  return (
    <div
      className={`flex flex-nowrap gap-[0.22rem] rounded-[0.45rem] p-[0.18rem] max-[640px]:flex-wrap max-[640px]:gap-[0.16rem] max-[640px]:p-[0.14rem] ${champGroupClassName[outcome]}`}
      aria-label={label}
    >
      {champions.map((champion) => (
        <img
          key={`${String(matchId)}-${gameKey}-${side}-${champion}`}
          src={championIconUrl(champion)}
          alt={champion}
          title={champion}
          className="h-[1.45rem] w-[1.45rem] rounded-[0.3rem] border border-white/14 object-cover max-[640px]:h-[1.3rem] max-[640px]:w-[1.3rem]"
          loading="lazy"
        />
      ))}
    </div>
  );
};

const GameChampRow = ({
  game,
  matchId,
  index,
  gameLabel,
  teamLabel,
  opponentLabel,
}: {
  game: ITeamMatchHistoryGame;
  matchId: number;
  index: number;
  gameLabel: string;
  teamLabel: string;
  opponentLabel: string;
}) => {
  const teamOutcome = outcomeFromResult(game.result);
  const opponentOutcome = game.result === "WIN" ? "loss" : game.result === "LOSS" ? "win" : "draw";
  const gameNumber = index + 1;
  const gameKey = `${String(game.riotGameId ?? 0)}-${String(game.gameNumber)}-${String(gameNumber)}`;
  const hasChamps = game.champions.length > 0 || game.opponentChampions.length > 0;

  if (!hasChamps) {
    return null;
  }

  return (
    <div
      className="flex min-w-0 items-center gap-[0.45rem] max-[900px]:w-full"
      aria-label={gameLabel.replace("{number}", String(gameNumber))}
    >
      <span
        className="min-w-[1.35rem] shrink-0 font-shentox text-[0.68rem] font-semibold tracking-[0.04em] text-white/45"
        aria-hidden="true"
      >
        G{gameNumber}
      </span>
      <div className="flex min-w-0 items-center gap-[0.55rem] max-[640px]:flex-wrap max-[640px]:gap-[0.4rem]">
        <ChampGroup
          champions={game.champions}
          outcome={teamOutcome}
          label={teamLabel}
          matchId={matchId}
          gameKey={gameKey}
          side="team"
        />
        {game.champions.length > 0 && game.opponentChampions.length > 0 ? (
          <span
            className="w-[0.2rem] min-h-[1.65rem] shrink-0 self-stretch rounded-full bg-white/28 max-[640px]:min-h-[1.3rem]"
            aria-hidden="true"
          />
        ) : null}
        <ChampGroup
          champions={game.opponentChampions}
          outcome={opponentOutcome}
          label={opponentLabel}
          matchId={matchId}
          gameKey={gameKey}
          side="opponent"
        />
      </div>
    </div>
  );
};

const historyRowBaseClassName =
  "grid w-full cursor-pointer items-center gap-x-[0.85rem] gap-y-3 rounded-[0.85rem] border border-white/8 bg-[var(--surface-card-bg)] p-[0.75rem_0.85rem] text-left text-inherit transition-[border-color,background,transform] duration-150 hover:-translate-y-px hover:border-[rgba(28,152,217,0.5)] hover:bg-white/4 focus-visible:-translate-y-px focus-visible:border-[rgba(28,152,217,0.5)] focus-visible:bg-white/4 focus-visible:outline-none max-[900px]:grid-cols-[auto_minmax(0,1fr)] max-[900px]:[grid-template-areas:'result_meta'_'matchup_matchup'_'champs_champs'] max-[900px]:gap-x-3 max-[900px]:gap-y-[0.6rem] max-[640px]:p-[0.7rem_0.75rem] grid-cols-[auto_minmax(5.5rem,7.5rem)_minmax(0,1fr)_auto]";

export const TeamMatchHistory = ({ teamId, teamName, entries }: TeamMatchHistoryProps) => {
  const { strings, locale } = useLocalization();
  const { localized } = useLocalePath();
  const navigate = useNavigate();
  const copy = strings.TEAMS;

  if (!entries.length) {
    return <p className={emptyTextClassName}>{copy.NO_MATCH_HISTORY}</p>;
  }

  return (
    <ul className="m-0 flex list-none flex-col gap-[0.65rem] p-0" aria-label={copy.TAB_HISTORY}>
      {entries.map((entry) => {
        const when = formatMatchDate(entry.time, locale, strings);
        const matchUrl = localized(matchDetailUrl(entry.matchId));
        const isWin = entry.result === "WIN";
        const isLoss = entry.result === "LOSS";
        const resultLabel = isWin ? copy.RESULT_WIN : isLoss ? copy.RESULT_LOSS : copy.RESULT_DRAW;
        const opponentName = entry.opponentTeamName || entry.opponentTeamId || strings.COMMON.TBD;
        const dateLabel = when?.date ?? "—";
        const games = entry.games;
        const hasChampions = games.some(
          (game) => game.champions.length > 0 || game.opponentChampions.length > 0,
        );

        return (
          <li key={entry.matchId}>
            <button
              type="button"
              className={`${historyRowBaseClassName} ${
                isWin
                  ? "border-l-[3px] border-l-[rgba(94,234,212,0.85)]"
                  : isLoss
                    ? "border-l-[3px] border-l-[rgba(248,113,113,0.75)]"
                    : ""
              }`.trim()}
              onClick={() => {
                void navigate(matchUrl);
              }}
              aria-label={copy.HISTORY_ROW_LABEL.replace("{opponent}", opponentName).replace(
                "{score}",
                `${String(entry.teamScore)}–${String(entry.opponentScore)}`,
              )}
            >
              <span
                className={`inline-flex h-[1.75rem] min-w-[1.75rem] items-center justify-center rounded-[0.4rem] font-shentox text-[0.78rem] font-bold tracking-[0.04em] max-[900px]:[grid-area:result] ${
                  isWin
                    ? "bg-[rgba(94,234,212,0.16)] text-[#5eead4]"
                    : isLoss
                      ? "bg-[rgba(248,113,113,0.16)] text-[#f87171]"
                      : "bg-white/6 text-white/85"
                }`}
                aria-hidden="true"
              >
                {resultLabel}
              </span>

              <div className="flex min-w-0 flex-col gap-[0.2rem] max-[900px]:[grid-area:meta]">
                <span className="truncate font-shentox text-[0.78rem] font-semibold tracking-[0.04em] text-white/78 uppercase">
                  {getMatchTypeLocalized(entry.matchType, strings)}
                </span>
                <time
                  className="truncate font-shentox text-[0.78rem] tracking-[0.04em] text-white/55"
                  dateTime={when?.dateTime ?? entry.time}
                >
                  {dateLabel}
                </time>
              </div>

              <div className="flex min-w-0 items-center gap-[0.55rem] max-[900px]:[grid-area:matchup]">
                <div className="flex min-w-0 items-center gap-[0.4rem]">
                  <TeamLogo
                    teamId={teamId}
                    alt={strings.COMMON.TEAM_LOGO_ALT.replace("{team}", teamName)}
                    className="h-[1.75rem] w-[1.75rem] shrink-0 rounded-[0.35rem] object-cover max-[640px]:h-6 max-[640px]:w-6"
                    linkToTeam={false}
                  />
                  <span className="font-shentox text-[1.05rem] font-bold text-white max-[640px]:text-[0.98rem]">
                    {entry.teamScore}
                  </span>
                </div>
                <span className="font-bold text-white/35" aria-hidden="true">
                  –
                </span>
                <div className="flex min-w-0 flex-1 items-center gap-[0.4rem]">
                  <span className="font-shentox text-[1.05rem] font-bold text-white max-[640px]:text-[0.98rem]">
                    {entry.opponentScore}
                  </span>
                  <TeamLogo
                    teamId={entry.opponentTeamId || "TBD"}
                    alt={strings.COMMON.TEAM_LOGO_ALT.replace("{team}", opponentName)}
                    className="h-[1.75rem] w-[1.75rem] shrink-0 rounded-[0.35rem] object-cover max-[640px]:h-6 max-[640px]:w-6"
                    linkToTeam={false}
                  />
                  <span className="truncate font-shentox text-[0.92rem] font-semibold text-white/88 max-[640px]:text-[0.85rem] max-[900px]:max-w-full">
                    {opponentName}
                  </span>
                </div>
              </div>

              <div
                className="flex min-w-0 flex-col items-end gap-[0.4rem] max-[900px]:[grid-area:champs] max-[900px]:w-full max-[900px]:items-start"
                aria-label={copy.CHAMPIONS_LABEL}
              >
                {hasChampions ? (
                  games.map((game, index) => (
                    <GameChampRow
                      key={`${String(entry.matchId)}-g-${String(game.riotGameId ?? index)}-${String(game.gameNumber)}`}
                      game={game}
                      matchId={entry.matchId}
                      index={index}
                      gameLabel={copy.GAME_CHAMPS_LABEL}
                      teamLabel={copy.TEAM_CHAMPIONS_LABEL}
                      opponentLabel={copy.OPPONENT_CHAMPIONS_LABEL}
                    />
                  ))
                ) : (
                  <span className="font-shentox text-[0.8rem] text-white/40">
                    {copy.NO_CHAMPIONS}
                  </span>
                )}
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
};
